import React from 'react';
import { CSSObject } from '@emotion/react';
import Avatar from '@cvent/blocks/components/BlockAvatar';
import { useTranslate } from 'nucleus-text';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { Speaker } from '@graphql/gql/graphql';
import useBreakpoints from '@hooks/useBreakpoints';
import useBlockTheme from '@cvent/blocks/hooks/useTheme';
import Typography from '@cvent/blocks/components/Typography';

const useStyles = (hasBiography = null): Record<string, CSSObject> => {
  const { mediaIsM, mediaIsLargeAndAbove, isDefaultSize } = useBreakpoints();
  const { font, size, backgroundColor } = useBlockTheme();

  return {
    container: {
      padding: 16,
      display: 'flex',
      flexDirection: isDefaultSize ? 'column' : 'row',
      alignItems: isDefaultSize ? 'flex-start' : 'center',
      borderRadius: size.border.radius.fiveX,
      backgroundColor: backgroundColor.base,
      width: '100%',
      '&:hover': {
        backgroundColor: hasBiography ? backgroundColor.hover : backgroundColor.base,
        cursor: hasBiography ? 'pointer' : 'auto'
      },
      border: 'none',
      [mediaIsLargeAndAbove]: {
        padding: 24
      }
    },
    name: {
      marginBottom: 0,
      fontSize: font.base.size.h3,
      color: font.color.base,
      lineHeight: font.lineHeight.base,
      fontWeight: 600
    },
    title: {
      fontSize: font.base.size['6'],
      color: font.color.base,
      lineHeight: font.lineHeight.base,
      textAlign: 'left'
    },
    descriptionContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      overflowWrap: 'anywhere'
    },
    avatarContainer: {
      paddingRight: 8,
      [mediaIsM]: {
        paddingRight: 16
      },
      [mediaIsLargeAndAbove]: {
        paddingRight: font.base.size[7]
      }
    }
  };
};

export type SpeakerCardTypes = {
  testID?: string;
  speaker: Pick<Speaker, 'biography' | 'firstName' | 'lastName' | 'profileImageUri' | 'title' | 'company'>;
  onClick?: (value) => void;
};

type DescriptionBlockTypes = {
  testID: string;
  firstName: string;
  lastName: string;
  title: string;
  company: string;
};

type SpeakerAvatarTypes = {
  testID: string;
  firstName: string;
  lastName: string;
  profileImageUri: string;
  avatarSize: number;
};

export function SpeakerAvatar({
  firstName,
  lastName,
  profileImageUri,
  testID,
  avatarSize
}: SpeakerAvatarTypes): JSX.Element {
  const { translate } = useTranslate();
  const { avatarContainer } = useStyles();

  return (
    <div css={avatarContainer}>
      <Avatar
        size={avatarSize}
        text={translate('Universal-AvatarName', {
          firstName,
          lastName
        })}
        image={profileImageUri}
        testID={testID}
      />
    </div>
  );
}

export function DescriptionBlock({ firstName, lastName, title, company, testID }: DescriptionBlockTypes): JSX.Element {
  const { translate } = useTranslate();
  const { name, descriptionContainer, title: titleStyle } = useStyles();

  const fullName = translate('Universal-FullName', {
    firstName,
    lastName
  });
  const speakerTitle = title ? translate('Speaker-Title', { title }) : title;
  const speakerCompany = company ? translate('Speaker-Company', { company }) : company;

  return (
    <div css={descriptionContainer}>
      <Typography css={name} testID={`${testID}-name`} as="h3">
        {fullName}
      </Typography>
      <div css={titleStyle} {...injectTestId(`${testID}-title-and-company`)}>
        {speakerTitle}
      </div>
      <div css={titleStyle} {...injectTestId(`${testID}-title-and-company`)}>
        {speakerCompany}
      </div>
    </div>
  );
}

function SpeakersCard({ speaker, testID = 'speaker-card', onClick }: SpeakerCardTypes): JSX.Element {
  const { container } = useStyles(Boolean(speaker.biography));
  const { isS, isM, isL } = useBreakpoints();
  const { translate } = useTranslate();

  let avatarSize = 8;
  if (isL || isM) {
    avatarSize = 6;
  } else if (isS) {
    avatarSize = 4;
  }
  return (
    <button
      type="button"
      css={container}
      onClick={onClick}
      aria-label={translate('Universal-FullName', {
        firstName: speaker.firstName,
        lastName: speaker.lastName
      })}
    >
      <SpeakerAvatar
        testID={testID}
        firstName={speaker.firstName}
        lastName={speaker.lastName}
        profileImageUri={speaker.profileImageUri}
        avatarSize={avatarSize}
      />
      <DescriptionBlock
        firstName={speaker.firstName}
        lastName={speaker.lastName}
        title={speaker.title}
        company={speaker.company}
        testID={testID}
      />
    </button>
  );
}

export default SpeakersCard;
